import clsx from 'clsx';
import type React from 'react';

import {
    AtomButton,
    type AtomButtonProps,
    AtomLink,
    ButtonIcon,
    type ButtonProps,
} from '../Button';
import type { Href } from '../Router';
import { Tooltip } from '../Tooltip';

/**
 * Header with navigation tabs in the table of contents.
 */
export function ButtonTabs(
    props: React.PropsWithChildren<{
        className?: string;
    }>
) {
    const { className, children } = props;

    return <div className={clsx('flex items-center gap-1', className)}>{children}</div>;
}

export type ButtonTabProps = {
    /** Label of the tab */
    label: string;
    /** Whether the tab is active */
    active: boolean;
    /** Callback when the tab is pressed */
    onPress?: AtomButtonProps['onPress'];
    /** Link to navigate to when the tab is pressed */
    href?: Href;
    /** Title to show in the tooltip */
    title?: string;
    /**
     * Icon positioned ahead of the label.
     */
    leadingIcon?: ButtonProps['leadingIcon'];

    /**
     * Icon positioned after the label.
     */
    trailingIcon?: ButtonProps['trailingIcon'];
};

/**
 * Button in a tab bar.
 */
export function ButtonTab(props: ButtonTabProps) {
    const { href, label, active, onPress, title, leadingIcon, trailingIcon } = props;

    const buttonProps = {
        className: clsx(
            'flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md border border-solid px-2 py-px text-ui-action data-[focus-visible]:shadow-focus-ring',
            active
                ? 'cursor-default border-base bg-side text-base'
                : 'cursor-pointer border-transparent bg-base text-muted hover:bg-standout-base'
        ),
        'aria-current': active,
    };

    const leadingIconSlot = leadingIcon ? <ButtonIcon icon={leadingIcon} size="small" /> : null;
    const trailingIconSlot = trailingIcon ? <ButtonIcon icon={trailingIcon} size="small" /> : null;
    return (
        <Tooltip label={title}>
            {href ? (
                <AtomLink {...buttonProps} href={href}>
                    {leadingIconSlot}
                    {label}
                    {trailingIconSlot}
                </AtomLink>
            ) : (
                <AtomButton {...buttonProps} onPress={onPress}>
                    {leadingIconSlot}
                    {label}
                    {trailingIconSlot}
                </AtomButton>
            )}
        </Tooltip>
    );
}
